import { formatRoute } from 'react-router-named-routes'
import { matchPath } from 'react-router-dom'
import {
    RouteProps,
    RoutePropsWithPathResolver,
} from 'types'
import routes from 'routes'
import { difference, uniqBy } from 'lodash'

const defaultGetPath = (path: string, params): string => {
    const formattedPath = formatRoute(path, params)

    return formattedPath
}

// Suboptimal solution.
// If we provide multiple paths we should probably overwrite getPath anyway.
const toSinglePath = (path: string | string[]) => (typeof path === 'string'
    ? path
    : path[0])

let nextId = 1
export const createRoute = <T>(route: RouteProps<T>): RoutePropsWithPathResolver<T> => ({
    ...route,
    title: route.title,
    id: nextId++,
    isEventRoute: route.isEventRoute || false,
    isPublic: route.isPublic || false,
    exact: route.exact !== false, // false if false, true if undefined
    /**
         * use getPath to get the path of a route. Example:
         * <Link to={routes.dashboard.getPath()} />
         * or if route accept params like "user/:id":
         * <Link to={routes.userDetails.getPath({ id: 1 })} />
         */
    getPath: route.getPath
        ? (params: T) => route.getPath(params)
        : (params: T) => defaultGetPath(
            // @ts-ignore
            toSinglePath(route.path), params
        ),
})

export const createEventRoute = <T>(
    route: Omit<RouteProps<T>, 'isEventRoute'>
): RoutePropsWithPathResolver<T & { eventSlug: string }> => createRoute<T & { eventSlug: string }>({
    ...route,
    isEventRoute: true,
    path: typeof route.path === 'string'
        ? `/:eventSlug${route.path}`
        : route.path.map((path) => `/:eventSlug${path}`),
})

export const getEnterEventPath = (path: string, eventSlug: string) => {
    // For invited email for booth's admin
    // Eg: https://portalv2.test.appendee.com/conferencetest/?boothdemo1/
    const boothSearch = new URLSearchParams(window.location.search).get('booth')
    if (boothSearch) {
        return `/${eventSlug}/enterevent?next=booth/${boothSearch}`
    }

    // For using the editing booth link directly
    // Eg: https://portalv2.test.appendee.com/conferencetest/booth/boothdemo1
    const boothPath = matchPath(path, routes.editBooth)
    if (!boothPath) {
        return `/${eventSlug}/enterevent`
    }

    const { boothSlug } = boothPath.params as { boothSlug: string }
    return `/${eventSlug}/enterevent?next=booth/${boothSlug}`
}

export const checkDuplicatedContext = () => {
    const routerItems = Object.values(routes)
    const uniqByContextItems = uniqBy(routerItems, 'context')
    if (routerItems.length > uniqByContextItems.length) {
        throw new Error(`The router's config has duplicated context: ${JSON.stringify(difference(routerItems, uniqByContextItems))}`)
    }
}
